/**
 * permissions.ts — page keys, role templates and the compact custom-claims encoding.
 * Shared by the web app (menus, route guards, the admin matrix) and Cloud Functions
 * (onUserWritten writes the claims, callables re-check them). See docs/PERMISSIONS.md.
 *
 * Claims (kept far below the 1000-byte limit):
 *   a: 1 active | 0 disabled
 *   p: "d2p1l0…" — one letter per page followed by its level (0 none, 1 read, 2 write)
 *   m: minyan scope, ['*'] = all
 *   x: 1 = may read sensitive personal fields (peoplePrivate, auditLogPrivate)
 */

export const PAGE_KEYS = [
  'dashboard', 'people', 'ledger', 'payments', 'boxes', 'bank', 'nedarim', 'services',
  'minyanim', 'calendar', 'events', 'reports', 'mailing', 'settings', 'users', 'audit',
] as const;

export type PageKey = (typeof PAGE_KEYS)[number];
export type Level = 0 | 1 | 2;
export type Perms = Record<PageKey, Level>;
export type TemplateId = 'admin' | 'gabbai' | 'secretary';
export type UserTemplate = TemplateId | 'custom';

/** Letter per page in the compact claim string. Must match firestore.rules. */
export const PAGE_CODES: Record<PageKey, string> = {
  dashboard: 'd', people: 'p', ledger: 'l', payments: 'y', boxes: 'b', bank: 'k', nedarim: 'n',
  services: 's', minyanim: 'm', calendar: 'c', events: 'v', reports: 'r', mailing: 'e', settings: 't', users: 'u', audit: 'a',
};

export const PAGE_LABELS_HE: Record<PageKey, string> = {
  dashboard: 'לוח בקרה', people: 'אנשי קשר', ledger: 'כרטסות וחובות', payments: 'תשלומים', boxes: 'קופות והוצאות',
  bank: 'התאמת בנק', nedarim: 'נדרים פלוס', services: 'מכירות ועליות', minyanim: 'מניינים ומצוות',
  calendar: 'יומן שנתי', events: 'אירועים', reports: 'דוחות', mailing: 'דיוור', settings: 'הגדרות', users: 'משתמשים', audit: 'יומן שינויים',
};

/** What else each permission opens (shown under the row in the users screen — owner report 2026-09-30). */
export const PAGE_HINTS_HE: Partial<Record<PageKey, string>> = {
  dashboard: 'כולל משימות. התראות (הו״ק, דמי חבר, הלוואות) — רק עם הרשאה לכספים',
  boxes: 'כולל ארנקים, תמיכות והלוואות — וגם אירועים',
  events: 'גם מי שיש לו "קופות והוצאות" רואה אירועים',
  services: 'רישום מכירות, עליות וחזן — במניינים שנבחרו',
  calendar: 'כולל הערות ביומן',
  nedarim: 'כולל תיבת השיוך, הקטגוריות ומרכז ההתראות',
};

export const LEVEL_LABELS_HE: Record<Level, string> = { 0: 'ללא', 1: 'צפייה', 2: 'עריכה' };

export const TEMPLATE_LABELS_HE: Record<UserTemplate, string> = {
  admin: 'מנהל', gabbai: 'גבאי מניין', secretary: 'מזכיר', custom: 'מותאם אישית',
};

const row = (...levels: Level[]): Perms =>
  Object.fromEntries(PAGE_KEYS.map((k, i) => [k, levels[i]])) as Perms;

/** docs/PERMISSIONS.md "Templates" — in PAGE_KEYS order. */
export const TEMPLATES: Record<TemplateId, { perms: Perms; pii: boolean; allMinyanim: boolean }> = {
  //                     d  p  l  y  b  k  n  s  m  c  v  r  e  t  u  a
  admin:     { perms: row(2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2), pii: true, allMinyanim: true },
  gabbai:    { perms: row(1, 1, 0, 0, 0, 0, 0, 2, 1, 1, 0, 0, 0, 0, 0, 0), pii: false, allMinyanim: false },
  secretary: { perms: row(1, 2, 2, 2, 2, 2, 2, 1, 1, 1, 2, 2, 2, 0, 0, 1), pii: true, allMinyanim: true },
};

export function emptyPerms(): Perms {
  return row(...PAGE_KEYS.map(() => 0 as Level));
}

export function normalizePerms(input: Partial<Record<string, unknown>> | null | undefined): Perms {
  const out = emptyPerms();
  for (const k of PAGE_KEYS) {
    const v = Number(input?.[k] ?? 0);
    out[k] = (v === 1 || v === 2 ? v : 0) as Level;
  }
  return out;
}

export function encodePerms(perms: Perms): string {
  return PAGE_KEYS.map((k) => PAGE_CODES[k] + (perms[k] ?? 0)).join('');
}

export function decodePerms(p: string | null | undefined): Perms {
  const out = emptyPerms();
  if (!p) return out;
  for (const k of PAGE_KEYS) {
    const m = new RegExp(PAGE_CODES[k] + '([012])').exec(p);
    if (m) out[k] = Number(m[1]) as Level;
  }
  return out;
}

export interface CompactClaims {
  a: 0 | 1;
  p: string;
  m: string[];
  x: 0 | 1;
  /** the treasurer's box (owner 2026-10-17): set only for the users who may see it (absent = no) */
  t?: 1;
}

export interface UserPermFields {
  active: boolean;
  perms: Partial<Record<string, unknown>>;
  minyanScope: string[];
  pii: boolean;
  treasurerBox?: boolean;
}

export function buildClaims(u: UserPermFields): CompactClaims {
  const scope = Array.isArray(u.minyanScope) ? u.minyanScope.filter((s) => typeof s === 'string' && s) : [];
  return {
    a: u.active ? 1 : 0,
    p: encodePerms(normalizePerms(u.perms)),
    m: scope.includes('*') ? ['*'] : [...new Set(scope)].sort(),
    x: u.pii ? 1 : 0,
    ...(u.treasurerBox ? { t: 1 as const } : {}),
  };
}

/** "קופת גזבר" (owner 2026-10-17): only the users marked on their card see it — not a page key, a flag like pii. */
export function hasTreasurerBox(claims: Partial<CompactClaims> | null | undefined): boolean {
  return !!claims && claims.a === 1 && claims.t === 1;
}

export function claimsEqual(a: CompactClaims | null | undefined, b: CompactClaims | null | undefined): boolean {
  if (!a || !b) return false;
  return a.a === b.a && a.p === b.p && a.x === b.x && (a.t ?? 0) === (b.t ?? 0) && a.m.length === b.m.length && a.m.every((v, i) => v === b.m[i]);
}

/**
 * A page whose screens were earlier covered by another permission keeps that access (owner request 2026-09-30:
 * events got their own key so someone can get events alone; everyone with "קופות והוצאות" still sees them).
 * Mirrored in firestore.rules (events: can1('v') || can1('b')).
 */
export const IMPLIED_BY: Partial<Record<PageKey, PageKey>> = { events: 'boxes' };

/** Level of a page from (possibly missing) claims. Disabled users get 0 everywhere. */
export function levelOf(claims: Partial<CompactClaims> | null | undefined, page: PageKey): Level {
  if (!claims || claims.a !== 1) return 0;
  const perms = decodePerms(claims.p);
  const via = IMPLIED_BY[page];
  return (via ? Math.max(perms[page], perms[via]) : perms[page]) as Level;
}

export function canRead(claims: Partial<CompactClaims> | null | undefined, page: PageKey): boolean {
  return levelOf(claims, page) >= 1;
}

export function canWrite(claims: Partial<CompactClaims> | null | undefined, page: PageKey): boolean {
  return levelOf(claims, page) === 2;
}

/**
 * Alerts (failed / stopped kevas, membership gaps, loans, Nedarim) are financial information about people: they are
 * for whoever reads Nedarim, payments, ledgers or boxes — not for "dashboard" alone (owner report 2026-10-01: a user
 * with dashboard + events saw the failed kevas). Mirrored in firestore.rules (alerts read).
 */
export const ALERT_PAGES: PageKey[] = ['nedarim', 'payments', 'ledger', 'boxes'];
export function canSeeAlerts(claims: Partial<CompactClaims> | null | undefined): boolean {
  return ALERT_PAGES.some((p) => levelOf(claims, p) >= 1);
}

export function inMinyanScope(claims: Partial<CompactClaims> | null | undefined, minyanId: string): boolean {
  const m = claims?.m ?? [];
  return m.includes('*') || m.includes(minyanId);
}

// ---------- usernames ----------

export const USER_EMAIL_DOMAIN = 'users.neevehapisga.com';
/** Lowercase Latin letters, digits and . _ - ; 3–32 chars; must start with a letter or digit. */
export const USERNAME_RE = /^[a-z0-9][a-z0-9._-]{2,31}$/;
export const MIN_PASSWORD_LENGTH = 8;

export function normalizeUsername(u: string): string {
  return String(u ?? '').trim().toLowerCase();
}

export function isValidUsername(u: string): boolean {
  return USERNAME_RE.test(normalizeUsername(u));
}

export function usernameToEmail(u: string): string {
  return `${normalizeUsername(u)}@${USER_EMAIL_DOMAIN}`;
}
